import React, { useState, useRef, useEffect } from 'react';

const POPOVER_GAP = 8;
const POPOVER_MAX_HEIGHT = 360;

function SidebarTagsPicker({ taggedItems: taggedItemsProp, tags: tagsProp, availableTags = [], onAdd, onRemove, onCreateTag }) {
  const useTaggedItems = Array.isArray(taggedItemsProp);
  const taggedItems = useTaggedItems
    ? taggedItemsProp
    : (tagsProp || []).map((t) => ({ id: t.id, tag: t }));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [search, setSearch] = useState('');
  const popoverRef = useRef(null);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const handleClick = (e) => {
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    const handleScrollOrResize = (e) => {
      if (e.type === 'scroll' && popoverRef.current && popoverRef.current.contains(e.target)) return;
      setPopoverOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [popoverOpen]);

  const openPopover = () => {
    const rect = buttonRef.current.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    const fitsBelow = rect.bottom + POPOVER_MAX_HEIGHT <= window.innerHeight;
    setPopoverPosition(fitsBelow
      ? { top: rect.bottom + POPOVER_GAP, right }
      : { bottom: window.innerHeight - rect.top + POPOVER_GAP, right });
    setSearch('');
    setPopoverOpen(true);
  };

  useEffect(() => {
    if (popoverOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [popoverOpen]);

  const appliedTagIds = taggedItems.map((item) => item.tag && item.tag.id).filter(Boolean);

  const filteredAvailable = availableTags.filter((tag) => {
    if (search && !(tag.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const canCreate = search.trim()
    && !availableTags.some((t) => t.name.toLowerCase() === search.trim().toLowerCase());

  const handleToggleTag = (tag) => {
    const taggedItem = taggedItems.find((item) => item.tag && item.tag.id === tag.id);
    if (taggedItem) {
      if (onRemove) onRemove(taggedItem.id);
    } else {
      if (onAdd) onAdd(tag);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className="cfm-prop-tags">
        {taggedItems
          .filter((item) => item.tag)
          .map((item) => (
            <span key={item.tag.id} className="cfm-tag-pill">
              {item.tag.name}
              {onRemove && (
                <button
                  type="button"
                  aria-label={`Remove ${item.tag.name}`}
                  onClick={() => onRemove(item.id)}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </span>
          ))}
        <button
          ref={buttonRef}
          type="button"
          className="cfm-add-tag-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (popoverOpen) {
              setPopoverOpen(false);
            } else {
              openPopover();
            }
          }}
        >
          + Add Tag
        </button>
      </div>

      {popoverOpen && (
        <div className="cfm-tag-manage-popup" ref={popoverRef} style={popoverPosition}>
          <div className="cfm-tag-manage-title">Manage Tags</div>
          <div className="cfm-tag-manage-search-wrap">
            <input
              ref={inputRef}
              type="text"
              className="cfm-tag-manage-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search or create tag..."
            />
          </div>
          <div className="cfm-tag-manage-list">
            {filteredAvailable.map((tag) => {
              const isActive = appliedTagIds.includes(tag.id);
              return (
                <div
                  key={tag.id}
                  className={`cfm-tag-toggle${isActive ? ' cfm-tag-toggle--active' : ''}`}
                  onClick={() => handleToggleTag(tag)}
                >
                  {tag.name}
                </div>
              );
            })}
            {canCreate && onCreateTag && (
              <div
                className="cfm-tag-toggle cfm-tag-toggle--create"
                onClick={() => {
                  onCreateTag(search.trim());
                  setSearch('');
                }}
              >
                + Create &quot;{search.trim()}&quot;
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SidebarTagsPicker;
